import { apiClient } from './api.service';
import type { UserPreferences } from './auth.service';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user';
  avatar?: string;
  preferences?: UserPreferences;
  isActive: boolean;
  isScimManaged: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  userTeams?: Array<{
    team: {
      id: string;
      name: string;
    };
  }>;
}

export interface CreateUserData {
  name: string;
  email: string;
  password?: string;
  role?: 'admin' | 'user';
  isActive?: boolean;
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  password?: string;
  role?: 'admin' | 'user';
  isActive?: boolean;
}

export interface UpdateProfileData {
  name?: string;
  avatar?: string;
}

export interface UpdatePreferencesData {
  theme?: 'light' | 'dark' | 'system';
  notifications?: {
    email?: boolean;
    browser?: boolean;
    riskAlerts?: boolean;
    weeklyDigest?: boolean;
  };
  dashboardLayout?: string;
  defaultSpaceId?: string;
}

export interface UserStatistics {
  total: number;
  active: number;
  inactive: number;
  admins: number;
  users: number;
}

// ============================================================================
// Admin endpoints (require admin role)
// ============================================================================

/**
 * Get all users (admin only)
 */
export const getAll = async (): Promise<User[]> => {
  return apiClient.get<User[]>('/users');
};

/**
 * Get user statistics (admin only)
 */
export const getStatistics = async (): Promise<UserStatistics> => {
  return apiClient.get<UserStatistics>('/users/statistics');
};

/**
 * Get user by ID (admin only)
 */
export const getById = async (id: string): Promise<User> => {
  return apiClient.get<User>(`/users/${id}`);
};

/**
 * Create new user (admin only)
 */
export const create = async (data: CreateUserData): Promise<User> => {
  return apiClient.post<User>('/users', data);
};

/**
 * Update user (admin only)
 */
export const update = async (
  id: string,
  data: UpdateUserData
): Promise<User> => {
  return apiClient.patch<User>(`/users/${id}`, data);
};

/**
 * Change user role (admin only)
 */
export const changeRole = async (
  id: string,
  role: 'admin' | 'user'
): Promise<User> => {
  return apiClient.patch<User>(`/users/${id}/role`, { role });
};

/**
 * Activate user (admin only)
 */
export const activate = async (id: string): Promise<User> => {
  return apiClient.patch<User>(`/users/${id}/activate`, {});
};

/**
 * Deactivate user (admin only)
 */
export const deactivate = async (id: string): Promise<User> => {
  return apiClient.patch<User>(`/users/${id}/deactivate`, {});
};

/**
 * Delete user (admin only)
 */
export const deleteUser = async (id: string): Promise<void> => {
  return apiClient.delete(`/users/${id}`);
};

// ============================================================================
// Self-service endpoints
// ============================================================================

/**
 * Get current user profile
 */
export const getMyProfile = async (): Promise<User> => {
  return apiClient.get<User>('/users/me/profile');
};

/**
 * Update current user profile
 */
export const updateMyProfile = async (
  data: UpdateProfileData
): Promise<User> => {
  return apiClient.patch<User>('/users/me/profile', data);
};

/**
 * Update current user preferences
 */
export const updateMyPreferences = async (
  data: UpdatePreferencesData
): Promise<User> => {
  return apiClient.patch<User>('/users/me/preferences', data);
};

export const userService = {
  // Admin
  getAll,
  getStatistics,
  getById,
  create,
  update,
  changeRole,
  activate,
  deactivate,
  delete: deleteUser,
  // Self-service
  getMyProfile,
  updateMyProfile,
  updateMyPreferences,
};
